// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";
import { enCA } from "date-fns/locale";

// mui
import {
  useTheme,
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Collapse,
  CardMedia,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import {
  B1Tiny,
  B3Tiny,
  ModalActionsLoadingButton,
  ModalContentOutlinedInput,
  ModalContentMenuItem,
  ModalContentSwitch,
  ModalTimePicker,
  ModalTimePickerSlotProps,
} from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Section Configuration
// ----------------------------------------------------------------------
const TIMING_INTRADAY_MODE_HELP_IMAGE = "/illustrations/converted/timing/intraday-mode.png";
const TIMING_INTRADAY_MODE_HELP_DESCRIPTION = (
  <>
    The Expert Advisor Builder offers a configuration option for an intraday trading mode. This feature is especially beneficial for scalping and day
    trading strategies that operate on shorter timeframes, such as M1 - M15. All input parameters associated with this feature are detailed in the
    section below. The feature comprises three time-based inputs and a toggle to turn the feature on or off. By default, the intraday feature is
    turned off.
    <br />
    <br />
    The illustration below showcases the operational principle of the intraday mode. In brief, each input parameter serves the following purposes:
    <ul>
      <li>
        <b>Start intraday trading:</b> This parameter sets the beginning of the time range when new trades can be opened. Existing trades can be
        maintained outside this time range, but new trades can only be initiated within the period defined by the start and stop time.
      </li>
      <li>
        <b>Stop intraday trading:</b> This parameter marks the end of the time range when new trades can be opened. Like the start parameter, existing
        trades can be sustained outside this time range, but new trades can only be opened within the trading period defined by the start and stop
        parameters.
      </li>
      <li>
        <b>Deadline intraday trading:</b> This parameter designates the start of the time range from which open trades are not permitted. Once this
        deadline is reached, all trades, irrespective of their performance, will be closed. This setting can be adjusted to match the swap deadline to
        avoid fees and unexpected market changes overnight.
      </li>
    </ul>
    These three input parameters work together with the timing input parameters discussed in the previous sections. As such, you can define a trading
    range, for instance, from 09:00:00 to 17:00:00, with a break from 11:00:00 to 14:00:00.{" "}
    <b>
      The time range when new trades cannot be opened, but existing ones can be maintained is colored orange. The time range during which no trade can
      be open is colored in red. All trades will be closed at the end of a red time range, regardless of their performance
    </b>
    . This feature can be used to avoid overnight swaps.
  </>
);

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TimingDefaults = {
  nVisualizeTiming: false,
  //
  strTradingMonths: "1-12",
  strTradingDaysOfWeek: "0-6",
  strTradingWeeks: "1-52",
  strTradingDays: "1-31",
  strTradingHours: "0-23",
  strTradingMinutes: "0-59",
  strTradingSeconds: "0-59",
  //
  nIntradayMode: false,
  strStartIntradayTrading: "09:00:00",
  strStopIntradayTrading: "18:00:00",
  strDeadlineIntradayTrading: "21:00:00",
  //
  nDstSummerShiftedMinutes: "0",
  nDstSummerShiftStartNumberOfMonth: "3",
  nDstSummerShiftStartDayOfWeek: "0",
  nDstSummerShiftStartCountDayOfWeekInMonth: "2",
  strDstSummerShiftStartTime: "02:00:00",
  nDstSummerShiftEndNumberOfMonth: "4",
  nDstSummerShiftEndDayOfWeek: "0",
  nDstSummerShiftEndCountDayOfWeekInMonth: "1",
  //
  strDstSummerShiftEndTime: "02:00:00",
  nDstWinterShiftedMinutes: "0",
  nDstWinterShiftStartNumberOfMonth: "10",
  nDstWinterShiftStartDayOfWeek: "0",
  nDstWinterShiftStartCountDayOfWeekInMonth: "2",
  strDstWinterShiftStartTime: "02:00:00",
  nDstWinterShiftEndNumberOfMonth: "11",
  nDstWinterShiftEndDayOfWeek: "0",
  nDstWinterShiftEndCountDayOfWeekInMonth: "1",
  strDstWinterShiftEndTime: "02:00:00",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nDstSummerShiftedMinutes: {},
  nDstWinterShiftedMinutes: {},
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function TimingRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  const getVisualizeTimingDisplay = () => {
    const value = config.nVisualizeTiming;
    return value === true ? "On" : "Off";
  };

  const getIntradayModeDisplay = () => {
    const value = config.nIntradayMode;
    return value === true ? "On" : "Off";
  };

  // Helper function to convert month numbers to text
  const getMonthsDisplay = (monthsString) => {
    if (!monthsString) return "1-12";

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // Parse the months string (e.g., "1-12" or "3,5,7")
    const months = [];
    const parts = monthsString.split(";");

    parts.forEach((part) => {
      part = part.trim();
      if (part.includes("-")) {
        const [start, end] = part.split("-").map((num) => parseInt(num.trim(), 10));
        for (let i = start; i <= end; i += 1) {
          if (i >= 1 && i <= 12) {
            months.push(monthNames[i - 1]);
          }
        }
      } else {
        const num = parseInt(part, 10);
        if (num >= 1 && num <= 12) {
          months.push(monthNames[num - 1]);
        }
      }
    });

    // Format the output
    if (months.length === 12) {
      return "January-December";
    }
    if (months.length === 1) {
      return months[0];
    }
    if (months.length === 2) {
      return months.join("-");
    }
    return months.join(", ");
  };

  // Helper function to convert day of week numbers to text
  const getDaysOfWeekDisplay = (daysString) => {
    if (!daysString) return "0-6";

    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    // Parse the days string (e.g., "0-6" or "1,3,5")
    const days = [];
    const parts = daysString.split(";");

    parts.forEach((part) => {
      part = part.trim();
      if (part.includes("-")) {
        const [start, end] = part.split("-").map((num) => parseInt(num.trim(), 10));
        for (let i = start; i <= end; i += 1) {
          if (i >= 0 && i <= 6) {
            days.push(dayNames[i]);
          }
        }
      } else {
        const num = parseInt(part, 10);
        if (num >= 0 && num <= 6) {
          days.push(dayNames[num]);
        }
      }
    });

    // Format the output
    if (days.length === 7) {
      return "Sunday-Saturday";
    }
    if (days.length === 1) {
      return days[0];
    }
    if (days.length === 2) {
      return days.join("-");
    }
    return days.join(", ");
  };

  // Helper to display DST parameters in readable format
  const getDstDisplay = (prefix) => {
    const month = config[`${prefix}NumberOfMonth`] || 0;
    const dayOfWeek = config[`${prefix}DayOfWeek`] || 0;
    const count = config[`${prefix}CountDayOfWeekInMonth`] || 0;
    // Convert prefix from "nDstWinterShiftStart" to "strDstWinterShiftStartTime"
    const timePrefix = prefix.replace("n", "str");
    const time = config[`${timePrefix}Time`] || "00:00:00";

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const countOrdinal = ["", "1st", "2nd", "3rd", "4th", "5th"];

    return `${monthNames[month - 1]} ${countOrdinal[count]} ${dayNames[dayOfWeek]} ${time}`;
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Visualize timing", getVisualizeTimingDisplay())}
      <Box>
        <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
      </Box>
      {renderParam("Trading months", getMonthsDisplay(config.strTradingMonths))}
      {renderParam("Trading days of week", getDaysOfWeekDisplay(config.strTradingDaysOfWeek))}
      {renderParam("Trading weeks", getDisplayValue("strTradingWeeks"))}
      {renderParam("Trading days", getDisplayValue("strTradingDays"))}
      {renderParam("Trading hours", getDisplayValue("strTradingHours"))}
      {renderParam("Trading minutes", getDisplayValue("strTradingMinutes"))}
      {renderParam("Trading seconds", getDisplayValue("strTradingSeconds"))}
      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("Intraday mode", getIntradayModeDisplay())}
          {config.nIntradayMode === true ? (
            <>
              {renderParam("Start intraday trading", getDisplayValue("strStartIntradayTrading"))}
              {renderParam("Stop intraday trading", getDisplayValue("strStopIntradayTrading"))}
              {renderParam("Deadline intraday trading", getDisplayValue("strDeadlineIntradayTrading"))}
            </>
          ) : null}
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("DST summer: Shifted minutes", getDisplayValue("nDstSummerShiftedMinutes"))}
          {renderParam("DST summer: Start", getDstDisplay("nDstSummerShiftStart"))}
          {renderParam("DST summer: End", getDstDisplay("nDstSummerShiftEnd"))}
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>
          {renderParam("DST winter: Shifted minutes", getDisplayValue("nDstWinterShiftedMinutes"))}
          {renderParam("DST winter: Start", getDstDisplay("nDstWinterShiftStart"))}
          {renderParam("DST winter: End", getDstDisplay("nDstWinterShiftEnd"))}
        </>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const TimingConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  TimingConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const autoCloseTimeoutRef = useRef(null);

  const theme = useTheme();

  const [collapseTiming, setCollapseTiming] = useState(false);

  // ----------------------------------------------------------------------
  // Helper functions for checkbox parsing
  // ----------------------------------------------------------------------

  // Helper function to parse seconds string to checkbox states
  const parseSecondsString = (secondsString) => {
    const selected = new Set();

    if (!secondsString) return selected;

    // Split by commas and process each part
    const parts = secondsString.split(";");

    parts.forEach((part) => {
      part = part.trim();

      if (part.includes("-")) {
        // Handle ranges like "0-59" or "10-20"
        const [start, end] = part.split("-").map((num) => parseInt(num.trim(), 10));
        if (!Number.isNaN(start) && !Number.isNaN(end)) {
          for (let i = start; i <= end; i += 1) {
            if (i >= 0 && i <= 59) {
              selected.add(i);
            }
          }
        }
      } else {
        // Handle individual numbers
        const num = parseInt(part, 10);
        if (!Number.isNaN(num) && num >= 0 && num <= 59) {
          selected.add(num);
        }
      }
    });

    return selected;
  };

  // Generic helper function to parse time string to checkbox states
  const parseTimeString = (timeString, min, max) => {
    const selected = new Set();

    if (!timeString) return selected;

    // Split by commas and process each part
    const parts = timeString.split(";");

    parts.forEach((part) => {
      part = part.trim();

      if (part.includes("-")) {
        // Handle ranges like "0-23" or "10-20"
        const [start, end] = part.split("-").map((num) => parseInt(num.trim(), 10));
        if (!Number.isNaN(start) && !Number.isNaN(end)) {
          for (let i = start; i <= end; i += 1) {
            if (i >= min && i <= max) {
              selected.add(i);
            }
          }
        }
      } else {
        // Handle individual numbers
        const num = parseInt(part, 10);
        if (!Number.isNaN(num) && num >= min && num <= max) {
          selected.add(num);
        }
      }
    });

    return selected;
  };

  // Helper function to convert checkbox states to string format
  const convertSelectedSecondsToString = (selectedSet) => {
    if (selectedSet.size === 0) return "";

    const sortedNumbers = Array.from(selectedSet).sort((a, b) => a - b);
    const ranges = [];
    let start = sortedNumbers[0];
    let end = sortedNumbers[0];

    for (let i = 1; i < sortedNumbers.length; i += 1) {
      if (sortedNumbers[i] === end + 1) {
        // Continue the range
        end = sortedNumbers[i];
      } else {
        // End the current range and start a new one
        if (start === end) {
          ranges.push(start.toString());
        } else {
          ranges.push(`${start}-${end}`);
        }
        start = sortedNumbers[i];
        end = sortedNumbers[i];
      }
    }

    // Add the last range
    if (start === end) {
      ranges.push(start.toString());
    } else {
      ranges.push(`${start}-${end}`);
    }

    return ranges.join(";");
  };

  // Generic helper function to convert checkbox states to string format
  const convertSelectedToString = (selectedSet) => {
    if (selectedSet.size === 0) return "";

    const sortedNumbers = Array.from(selectedSet).sort((a, b) => a - b);
    const ranges = [];
    let start = sortedNumbers[0];
    let end = sortedNumbers[0];

    for (let i = 1; i < sortedNumbers.length; i += 1) {
      if (sortedNumbers[i] === end + 1) {
        // Continue the range
        end = sortedNumbers[i];
      } else {
        // End the current range and start a new one
        if (start === end) {
          ranges.push(start.toString());
        } else {
          ranges.push(`${start}-${end}`);
        }
        start = sortedNumbers[i];
        end = sortedNumbers[i];
      }
    }

    // Add the last range
    if (start === end) {
      ranges.push(start.toString());
    } else {
      ranges.push(`${start}-${end}`);
    }

    return ranges.join(";");
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------

  // Add state to track checkbox selections
  const [selectedSeconds, setSelectedSeconds] = useState(() => {
    // Initialize from initial data or default "0-59"
    const initialValue = initialData?.strTradingSeconds ?? TimingDefaults.strTradingSeconds;
    return parseSecondsString(initialValue);
  });

  const [selectedMinutes, setSelectedMinutes] = useState(() => {
    const initialValue = initialData?.strTradingMinutes ?? TimingDefaults.strTradingMinutes;
    return parseTimeString(initialValue, 0, 59);
  });

  const [selectedHours, setSelectedHours] = useState(() => {
    const initialValue = initialData?.strTradingHours ?? TimingDefaults.strTradingHours;
    return parseTimeString(initialValue, 0, 23);
  });

  const [selectedDays, setSelectedDays] = useState(() => {
    const initialValue = initialData?.strTradingDays ?? TimingDefaults.strTradingDays;
    return parseTimeString(initialValue, 1, 31);
  });

  const [selectedWeeks, setSelectedWeeks] = useState(() => {
    const initialValue = initialData?.strTradingWeeks ?? TimingDefaults.strTradingWeeks;
    return parseTimeString(initialValue, 1, 52);
  });

  const [selectedMonths, setSelectedMonths] = useState(() => {
    const initialValue = initialData?.strTradingMonths ?? TimingDefaults.strTradingMonths;
    return parseTimeString(initialValue, 1, 12);
  });

  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState(() => {
    const initialValue = initialData?.strTradingDaysOfWeek ?? TimingDefaults.strTradingDaysOfWeek;
    return parseTimeString(initialValue, 0, 6);
  });

  // Sync checkbox states when initialData changes (e.g. switching modules)
  useEffect(() => {
    setSelectedSeconds(parseSecondsString(initialData?.strTradingSeconds ?? TimingDefaults.strTradingSeconds));
    setSelectedMinutes(parseTimeString(initialData?.strTradingMinutes ?? TimingDefaults.strTradingMinutes, 0, 59));
    setSelectedHours(parseTimeString(initialData?.strTradingHours ?? TimingDefaults.strTradingHours, 0, 23));
    setSelectedDays(parseTimeString(initialData?.strTradingDays ?? TimingDefaults.strTradingDays, 1, 31));
    setSelectedWeeks(parseTimeString(initialData?.strTradingWeeks ?? TimingDefaults.strTradingWeeks, 1, 52));
    setSelectedMonths(parseTimeString(initialData?.strTradingMonths ?? TimingDefaults.strTradingMonths, 1, 12));
    setSelectedDaysOfWeek(parseTimeString(initialData?.strTradingDaysOfWeek ?? TimingDefaults.strTradingDaysOfWeek, 0, 6));
  }, [initialData]);

  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});

    // Apply all the default values to the form
    Object.keys(TimingDefaults).forEach((key) => {
      setFieldValue(key, TimingDefaults[key]);
    });

    // Reset selected checkboxes
    setSelectedSeconds(parseSecondsString(TimingDefaults.strTradingSeconds));
    setSelectedMinutes(parseTimeString(TimingDefaults.strTradingMinutes, 0, 59));
    setSelectedHours(parseTimeString(TimingDefaults.strTradingHours, 0, 23));
    setSelectedDays(parseTimeString(TimingDefaults.strTradingDays, 1, 31));
    setSelectedWeeks(parseTimeString(TimingDefaults.strTradingWeeks, 1, 52));
    setSelectedMonths(parseTimeString(TimingDefaults.strTradingMonths, 1, 12));
    setSelectedDaysOfWeek(parseTimeString(TimingDefaults.strTradingDaysOfWeek, 0, 6));
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Helper functions for time picker handling
  // ----------------------------------------------------------------------
  const parseTimePickerString = (timeString) => {
    if (!timeString || timeString === "00:00:00") {
      return dayjs("2000-01-01T00:00:00");
    }
    return dayjs(`2000-01-01T${timeString}`);
  };

  const formatTimeString = (timeValue) => (timeValue ? timeValue.format("HH:mm:ss") : "00:00:00");

  // Handle checkbox change
  const handleSecondCheckboxChange = (second, checked) => {
    setSelectedSeconds((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(second);
      } else {
        newSelected.delete(second);
      }

      // Update the formik field value
      const newStringValue = convertSelectedSecondsToString(newSelected);
      setFieldValue("strTradingSeconds", newStringValue);

      return newSelected;
    });
  };

  const handleMinuteCheckboxChange = (minute, checked) => {
    setSelectedMinutes((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(minute);
      } else {
        newSelected.delete(minute);
      }

      const newStringValue = convertSelectedToString(newSelected);
      setFieldValue("strTradingMinutes", newStringValue);

      return newSelected;
    });
  };

  const handleHourCheckboxChange = (hour, checked) => {
    setSelectedHours((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(hour);
      } else {
        newSelected.delete(hour);
      }

      const newStringValue = convertSelectedToString(newSelected);
      setFieldValue("strTradingHours", newStringValue);

      return newSelected;
    });
  };

  const handleDayCheckboxChange = (day, checked) => {
    setSelectedDays((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(day);
      } else {
        newSelected.delete(day);
      }

      const newStringValue = convertSelectedToString(newSelected);
      setFieldValue("strTradingDays", newStringValue);

      return newSelected;
    });
  };

  const handleWeekCheckboxChange = (week, checked) => {
    setSelectedWeeks((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(week);
      } else {
        newSelected.delete(week);
      }

      const newStringValue = convertSelectedToString(newSelected);
      setFieldValue("strTradingWeeks", newStringValue);

      return newSelected;
    });
  };

  const handleMonthCheckboxChange = (month, checked) => {
    setSelectedMonths((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(month);
      } else {
        newSelected.delete(month);
      }

      const newStringValue = convertSelectedToString(newSelected);
      setFieldValue("strTradingMonths", newStringValue);

      return newSelected;
    });
  };

  const handleDayOfWeekCheckboxChange = (dayOfWeek, checked) => {
    setSelectedDaysOfWeek((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(dayOfWeek);
      } else {
        newSelected.delete(dayOfWeek);
      }

      const newStringValue = convertSelectedToString(newSelected);
      setFieldValue("strTradingDaysOfWeek", newStringValue);

      return newSelected;
    });
  };

  // ----------------------------------------------------------------------
  // Helper functions to parse initial DST values from dense format or existing split values
  // ----------------------------------------------------------------------
  const getInitialDstValues = () => {
    // If we have split values in initialData, use them
    if (initialData?.nDstSummerShiftStartNumberOfMonth !== undefined) {
      return {
        summerStart: {
          month: initialData.nDstSummerShiftStartNumberOfMonth,
          dayOfWeek: initialData.nDstSummerShiftStartDayOfWeek,
          count: initialData.nDstSummerShiftStartCountDayOfWeekInMonth,
          time: initialData.strDstSummerShiftStartTime,
        },
        summerEnd: {
          month: initialData.nDstSummerShiftEndNumberOfMonth,
          dayOfWeek: initialData.nDstSummerShiftEndDayOfWeek,
          count: initialData.nDstSummerShiftEndCountDayOfWeekInMonth,
          time: initialData.strDstSummerShiftEndTime,
        },
        winterStart: {
          month: initialData.nDstWinterShiftStartNumberOfMonth,
          dayOfWeek: initialData.nDstWinterShiftStartDayOfWeek,
          count: initialData.nDstWinterShiftStartCountDayOfWeekInMonth,
          time: initialData.strDstWinterShiftStartTime,
        },
        winterEnd: {
          month: initialData.nDstWinterShiftEndNumberOfMonth,
          dayOfWeek: initialData.nDstWinterShiftEndDayOfWeek,
          count: initialData.nDstWinterShiftEndCountDayOfWeekInMonth,
          time: initialData.strDstWinterShiftEndTime,
        },
      };
    }
  };

  const initialDstValues = getInitialDstValues();

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nVisualizeTiming: initialData?.nVisualizeTiming ?? TimingDefaults.nVisualizeTiming,
      //
      strTradingDaysOfWeek: initialData?.strTradingDaysOfWeek ?? TimingDefaults.strTradingDaysOfWeek,
      strTradingMonths: initialData?.strTradingMonths ?? TimingDefaults.strTradingMonths,
      strTradingWeeks: initialData?.strTradingWeeks ?? TimingDefaults.strTradingWeeks,
      strTradingDays: initialData?.strTradingDays ?? TimingDefaults.strTradingDays,
      strTradingHours: initialData?.strTradingHours ?? TimingDefaults.strTradingHours,
      strTradingMinutes: initialData?.strTradingMinutes ?? TimingDefaults.strTradingMinutes,
      strTradingSeconds: initialData?.strTradingSeconds ?? TimingDefaults.strTradingSeconds,
      //
      nIntradayMode: initialData?.nIntradayMode ?? TimingDefaults.nIntradayMode,
      strStartIntradayTrading: initialData?.strStartIntradayTrading ?? TimingDefaults.strStartIntradayTrading,
      strStopIntradayTrading: initialData?.strStopIntradayTrading ?? TimingDefaults.strStopIntradayTrading,
      strDeadlineIntradayTrading: initialData?.strDeadlineIntradayTrading ?? TimingDefaults.strDeadlineIntradayTrading,
      //
      nDstSummerShiftedMinutes: initialData?.nDstSummerShiftedMinutes ?? TimingDefaults.nDstSummerShiftedMinutes,
      nDstSummerShiftStartNumberOfMonth: initialDstValues.summerStart.month,
      nDstSummerShiftStartDayOfWeek: initialDstValues.summerStart.dayOfWeek,
      nDstSummerShiftStartCountDayOfWeekInMonth: initialDstValues.summerStart.count,
      strDstSummerShiftStartTime: initialDstValues.summerStart.time,
      nDstSummerShiftEndNumberOfMonth: initialDstValues.summerEnd.month,
      nDstSummerShiftEndDayOfWeek: initialDstValues.summerEnd.dayOfWeek,
      nDstSummerShiftEndCountDayOfWeekInMonth: initialDstValues.summerEnd.count,
      strDstSummerShiftEndTime: initialDstValues.summerEnd.time,
      //
      nDstWinterShiftedMinutes: initialData?.nDstWinterShiftedMinutes ?? TimingDefaults.nDstWinterShiftedMinutes,
      nDstWinterShiftStartNumberOfMonth: initialDstValues.winterStart.month,
      nDstWinterShiftStartDayOfWeek: initialDstValues.winterStart.dayOfWeek,
      nDstWinterShiftStartCountDayOfWeekInMonth: initialDstValues.winterStart.count,
      strDstWinterShiftStartTime: initialDstValues.winterStart.time,
      nDstWinterShiftEndNumberOfMonth: initialDstValues.winterEnd.month,
      nDstWinterShiftEndDayOfWeek: initialDstValues.winterEnd.dayOfWeek,
      nDstWinterShiftEndCountDayOfWeekInMonth: initialDstValues.winterEnd.count,
      strDstWinterShiftEndTime: initialDstValues.winterEnd.time,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          // Parse checkbox states and update values
          const finalValues = {
            ...values,
            strTradingMonths: convertSelectedToString(selectedMonths),
            strTradingDaysOfWeek: convertSelectedToString(selectedDaysOfWeek),
            strTradingWeeks: convertSelectedToString(selectedWeeks),
            strTradingDays: convertSelectedToString(selectedDays),
            strTradingHours: convertSelectedToString(selectedHours),
            strTradingMinutes: convertSelectedToString(selectedMinutes),
            strTradingSeconds: convertSelectedSecondsToString(selectedSeconds),
          };

          // Remove the dense format fields as they should not be saved in the API - only used for interim HMI interactions
          delete finalValues.strDstSummerShiftStart;
          delete finalValues.strDstSummerShiftEnd;
          delete finalValues.strDstWinterShiftStart;
          delete finalValues.strDstWinterShiftEnd;

          onSave(finalValues);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // Parse time strings for time pickers
  const startIntradayTime = parseTimePickerString(values.strStartIntradayTrading);
  const stopIntradayTime = parseTimePickerString(values.strStopIntradayTrading);
  const deadlineIntradayTime = parseTimePickerString(values.strDeadlineIntradayTrading);

  // Parse DST time strings for time pickers
  const dstSummerStartTime = parseTimePickerString(values.strDstSummerShiftStartTime);
  const dstSummerEndTime = parseTimePickerString(values.strDstSummerShiftEndTime);
  const dstWinterStartTime = parseTimePickerString(values.strDstWinterShiftStartTime);
  const dstWinterEndTime = parseTimePickerString(values.strDstWinterShiftEndTime);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure timing
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure timing</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nVisualizeTiming */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Visualize timing"
                      helpReference="HandleTiming"
                      helpText=""
                      helpLink=""
                      fieldName="nVisualizeTiming"
                      touched={touched}
                      errors={errors}
                    />
                    <ModalContentSwitch
                      sx={{ mt: "12px" }}
                      checked={values.nVisualizeTiming === true}
                      onChange={(event) => {
                        setFieldValue("nVisualizeTiming", event.target.checked);
                      }}
                    />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* strTradingMonths */}
                    {/* ---------------------------------------------------------------------- */}

                    <Box>
                      <SmartParameter
                        value="Trading months"
                        helpReference="HandleTiming"
                        helpText=""
                        helpLink=""
                        fieldName="strTradingMonths"
                        touched={touched}
                        errors={errors}
                      />
                      <FormGroup
                        sx={{
                          gap: 0,
                          "& .MuiFormControlLabel-root": {
                            marginTop: "-6px",
                            marginBottom: "-6px",
                            "& .MuiFormControlLabel-label": {
                              ...theme.typography.contentTiny,
                            },
                          },
                          "& .MuiCheckbox-root.Mui-checked": {
                            color: "#73E555",
                          },
                        }}
                      >
                        {[
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ].map((month, index) => (
                          <FormControlLabel
                            key={index + 1}
                            size="small"
                            control={
                              <Checkbox
                                checked={selectedMonths.has(index + 1)}
                                onChange={(event) => handleMonthCheckboxChange(index + 1, event.target.checked)}
                              />
                            }
                            label={month}
                          />
                        ))}
                      </FormGroup>
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* strTradingDaysOfWeek */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Trading days of week"
                        helpReference="HandleTiming"
                        helpText=""
                        helpLink=""
                        fieldName="strTradingDaysOfWeek"
                        touched={touched}
                        errors={errors}
                      />
                      <FormGroup
                        sx={{
                          gap: 0,
                          "& .MuiFormControlLabel-root": {
                            marginTop: "-6px",
                            marginBottom: "-6px",
                            "& .MuiFormControlLabel-label": {
                              ...theme.typography.contentSmall,
                            },
                          },
                          "& .MuiCheckbox-root.Mui-checked": {
                            color: "#73E555",
                          },
                        }}
                      >
                        {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, index) => (
                          <FormControlLabel
                            key={index}
                            size="small"
                            control={
                              <Checkbox
                                checked={selectedDaysOfWeek.has(index)}
                                onChange={(event) => handleDayOfWeekCheckboxChange(index, event.target.checked)}
                              />
                            }
                            label={day}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strTradingWeeks */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Trading weeks"
                      helpReference="HandleTiming"
                      helpText=""
                      helpLink=""
                      fieldName="strTradingWeeks"
                      touched={touched}
                      errors={errors}
                    />
                    <FormGroup
                      row
                      sx={{
                        gap: 0,
                        "& .MuiFormControlLabel-root": {
                          marginRight: "12px",
                          marginLeft: 0,
                          "& .MuiFormControlLabel-label": {
                            marginTop: "-12px",
                            marginBottom: "-6px",
                            ...theme.typography.contentTiny,
                          },
                        },
                        "& .MuiCheckbox-root.Mui-checked": {
                          color: "#73E555",
                        },
                      }}
                    >
                      {Array.from({ length: 52 }, (_, index) => (
                        <FormControlLabel
                          key={index + 1}
                          labelPlacement="bottom"
                          size="small"
                          control={
                            <Checkbox
                              checked={selectedWeeks.has(index + 1)}
                              onChange={(event) => handleWeekCheckboxChange(index + 1, event.target.checked)}
                            />
                          }
                          label={String(index + 1)}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strTradingDays */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Trading days"
                      helpReference="HandleTiming"
                      helpText=""
                      helpLink=""
                      fieldName="strTradingDays"
                      touched={touched}
                      errors={errors}
                    />
                    <FormGroup
                      row
                      sx={{
                        gap: 0,
                        "& .MuiFormControlLabel-root": {
                          marginRight: "22px",
                          marginLeft: 0,
                          "& .MuiFormControlLabel-label": {
                            marginTop: "-12px",
                            marginBottom: "-6px",
                            ...theme.typography.contentTiny,
                          },
                        },
                        "& .MuiCheckbox-root.Mui-checked": {
                          color: "#73E555",
                        },
                      }}
                    >
                      {Array.from({ length: 31 }, (_, index) => (
                        <FormControlLabel
                          key={index + 1}
                          labelPlacement="bottom"
                          size="small"
                          control={
                            <Checkbox
                              checked={selectedDays.has(index + 1)}
                              onChange={(event) => handleDayCheckboxChange(index + 1, event.target.checked)}
                            />
                          }
                          label={String(index + 1)}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strTradingHours */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Trading hours"
                      helpReference="HandleTiming"
                      helpText=""
                      helpLink=""
                      fieldName="strTradingHours"
                      touched={touched}
                      errors={errors}
                    />
                    <FormGroup
                      row
                      sx={{
                        gap: 0,
                        "& .MuiFormControlLabel-root": {
                          marginRight: "16px",
                          marginLeft: 0,
                          "& .MuiFormControlLabel-label": {
                            marginTop: "-12px",
                            marginBottom: "-6px",
                            ...theme.typography.contentTiny,
                          },
                        },
                        "& .MuiCheckbox-root.Mui-checked": {
                          color: "#73E555",
                        },
                      }}
                    >
                      {Array.from({ length: 24 }, (_, index) => (
                        <FormControlLabel
                          key={index}
                          labelPlacement="bottom"
                          size="small"
                          control={
                            <Checkbox
                              checked={selectedHours.has(index)}
                              onChange={(event) => handleHourCheckboxChange(index, event.target.checked)}
                            />
                          }
                          label={String(index)}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strTradingMinutes */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Trading minutes"
                      helpReference="HandleTiming"
                      helpText=""
                      helpLink=""
                      fieldName="strTradingMinutes"
                      touched={touched}
                      errors={errors}
                    />
                    <FormGroup
                      row
                      sx={{
                        gap: 0,
                        "& .MuiFormControlLabel-root": {
                          marginRight: "4px",
                          marginLeft: 0,
                          "& .MuiFormControlLabel-label": {
                            marginTop: "-12px",
                            marginBottom: "-6px",
                            ...theme.typography.contentTiny,
                          },
                        },
                        "& .MuiCheckbox-root.Mui-checked": {
                          color: "#73E555",
                        },
                      }}
                    >
                      {Array.from({ length: 60 }, (_, index) => (
                        <FormControlLabel
                          key={index}
                          labelPlacement="bottom"
                          size="small"
                          control={
                            <Checkbox
                              checked={selectedMinutes.has(index)}
                              onChange={(event) => handleMinuteCheckboxChange(index, event.target.checked)}
                            />
                          }
                          label={String(index)}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* strTradingSeconds */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Trading seconds"
                      helpReference="HandleTiming"
                      helpText=""
                      helpLink=""
                      fieldName="strTradingSeconds"
                      touched={touched}
                      errors={errors}
                    />
                    <FormGroup
                      row
                      sx={{
                        gap: 0,
                        "& .MuiFormControlLabel-root": {
                          marginRight: "4px",
                          marginLeft: 0,
                          "& .MuiFormControlLabel-label": {
                            marginTop: "-12px",
                            marginBottom: "-6px",
                            ...theme.typography.contentTiny,
                          },
                        },
                        "& .MuiCheckbox-root.Mui-checked": {
                          color: "#73E555",
                        },
                      }}
                    >
                      {Array.from({ length: 60 }, (_, index) => (
                        <FormControlLabel
                          key={index}
                          labelPlacement="bottom"
                          size="small"
                          control={
                            <Checkbox
                              checked={selectedSeconds.has(index)}
                              onChange={(event) => handleSecondCheckboxChange(index, event.target.checked)}
                            />
                          }
                          label={String(index)}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nIntradayMode */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="Intraday mode"
                          helpReference="HandleTiming"
                          helpText=""
                          helpLink=""
                          fieldName="nIntradayMode"
                          touched={touched}
                          errors={errors}
                        />
                        <ModalContentSwitch
                          sx={{ mt: "12px" }}
                          checked={values.nIntradayMode === true}
                          onChange={(event) => {
                            setFieldValue("nIntradayMode", event.target.checked);
                          }}
                        />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Help Sections */}
                      {/* ---------------------------------------------------------------------- */}
                      {TIMING_INTRADAY_MODE_HELP_IMAGE && (
                        <Box>
                          <B3Tiny
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setCollapseTiming(!collapseTiming);
                            }}
                          >
                            {collapseTiming ? "Hide help section" : "Show help section"}
                          </B3Tiny>

                          <Collapse sx={{ p: "3px" }} in={collapseTiming} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1 }}>
                              <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                                {TIMING_INTRADAY_MODE_HELP_DESCRIPTION}
                                <br />
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={TIMING_INTRADAY_MODE_HELP_IMAGE}
                                alt="Timing intraday mode"
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: "341px",
                                }}
                              />
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      {values.nIntradayMode === true ? (
                        <>
                          {/* ---------------------------------------------------------------------- */}
                          {/* Grid */}
                          {/* ---------------------------------------------------------------------- */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr",
                              gap: 2,
                            }}
                          >
                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* strStartIntradayTrading */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Start intraday trading"
                                helpReference="HandleTiming"
                                helpText=""
                                helpLink=""
                                fieldName="strStartIntradayTrading"
                                touched={touched}
                                errors={errors}
                              />
                              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                                <ModalTimePicker
                                  value={startIntradayTime}
                                  onChange={(value) => {
                                    const newTimeString = formatTimeString(value);
                                    setFieldValue("strStartIntradayTrading", newTimeString);
                                  }}
                                  ampm={false}
                                  views={["hours", "minutes", "seconds"]}
                                  slotProps={ModalTimePickerSlotProps}
                                />
                              </LocalizationProvider>
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* strStopIntradayTrading */}
                            {/* ---------------------------------------------------------------------- */}

                            <Box>
                              <SmartParameter
                                value="Stop intraday trading"
                                helpReference="HandleTiming"
                                helpText=""
                                helpLink=""
                                fieldName="strStopIntradayTrading"
                                touched={touched}
                                errors={errors}
                              />
                              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                                <ModalTimePicker
                                  value={stopIntradayTime}
                                  onChange={(value) => {
                                    const newTimeString = formatTimeString(value);
                                    setFieldValue("strStopIntradayTrading", newTimeString);
                                  }}
                                  ampm={false}
                                  views={["hours", "minutes", "seconds"]}
                                  slotProps={ModalTimePickerSlotProps}
                                />
                              </LocalizationProvider>
                            </Box>

                            {/* ---------------------------------------------------------------------- */}
                            {/* Parameter configuration */}
                            {/* strDeadlineIntradayTrading */}
                            {/* ---------------------------------------------------------------------- */}
                            <Box>
                              <SmartParameter
                                value="Deadline intraday trading"
                                helpReference="HandleTiming"
                                helpText=""
                                helpLink=""
                                fieldName="strDeadlineIntradayTrading"
                                touched={touched}
                                errors={errors}
                              />
                              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                                <ModalTimePicker
                                  value={deadlineIntradayTime}
                                  onChange={(value) => {
                                    const newTimeString = formatTimeString(value);
                                    setFieldValue("strDeadlineIntradayTrading", newTimeString);
                                  }}
                                  ampm={false}
                                  views={["hours", "minutes", "seconds"]}
                                  slotProps={ModalTimePickerSlotProps}
                                />
                              </LocalizationProvider>
                            </Box>
                          </Box>
                        </>
                      ) : null}

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nDstSummerShiftedMinutes */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="DST summer: Shifted minutes"
                          helpReference="HandleTiming"
                          helpText=""
                          helpLink=""
                          fieldName="nDstSummerShiftedMinutes"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nDstSummerShiftedMinutes" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nDstSummerShiftStart */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                          <Box>
                            <SmartParameter
                              value="DST summer: Start month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstSummerShiftStartNumberOfMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstSummerShiftStartNumberOfMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstSummerShiftStartNumberOfMonth && errors.nDstSummerShiftStartNumberOfMonth)}
                              >
                                <ModalContentMenuItem value="1">January</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">February</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">March</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">April</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">May</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">June</ModalContentMenuItem>
                                <ModalContentMenuItem value="7">July</ModalContentMenuItem>
                                <ModalContentMenuItem value="8">August</ModalContentMenuItem>
                                <ModalContentMenuItem value="9">September</ModalContentMenuItem>
                                <ModalContentMenuItem value="10">October</ModalContentMenuItem>
                                <ModalContentMenuItem value="11">November</ModalContentMenuItem>
                                <ModalContentMenuItem value="12">December</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST summer: Start day of week"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstSummerShiftStartDayOfWeek"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstSummerShiftStartDayOfWeek")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstSummerShiftStartDayOfWeek && errors.nDstSummerShiftStartDayOfWeek)}
                              >
                                <ModalContentMenuItem value="0">Sunday</ModalContentMenuItem>
                                <ModalContentMenuItem value="1">Monday</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">Tuesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">Wednesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">Thursday</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">Friday</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">Saturday</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST summer: Start week in month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstSummerShiftStartCountDayOfWeekInMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstSummerShiftStartCountDayOfWeekInMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstSummerShiftStartCountDayOfWeekInMonth && errors.nDstSummerShiftStartCountDayOfWeekInMonth)}
                              >
                                <ModalContentMenuItem value="1">1st</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">2nd</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">3rd</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">4th</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">5th</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST summer: Start time"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="strDstSummerShiftStartTime"
                              touched={touched}
                              errors={errors}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                              <ModalTimePicker
                                value={dstSummerStartTime}
                                onChange={(value) => {
                                  const newTimeString = formatTimeString(value);
                                  setFieldValue("strDstSummerShiftStartTime", newTimeString);
                                }}
                                ampm={false}
                                views={["hours", "minutes", "seconds"]}
                                slotProps={ModalTimePickerSlotProps}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Box>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nDstSummerShiftEnd */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                          <Box>
                            <SmartParameter
                              value="DST summer: End month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstSummerShiftEndNumberOfMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstSummerShiftEndNumberOfMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstSummerShiftEndNumberOfMonth && errors.nDstSummerShiftEndNumberOfMonth)}
                              >
                                <ModalContentMenuItem value="1">January</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">February</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">March</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">April</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">May</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">June</ModalContentMenuItem>
                                <ModalContentMenuItem value="7">July</ModalContentMenuItem>
                                <ModalContentMenuItem value="8">August</ModalContentMenuItem>
                                <ModalContentMenuItem value="9">September</ModalContentMenuItem>
                                <ModalContentMenuItem value="10">October</ModalContentMenuItem>
                                <ModalContentMenuItem value="11">November</ModalContentMenuItem>
                                <ModalContentMenuItem value="12">December</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST summer: End day of week"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstSummerShiftEndDayOfWeek"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstSummerShiftEndDayOfWeek")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstSummerShiftEndDayOfWeek && errors.nDstSummerShiftEndDayOfWeek)}
                              >
                                <ModalContentMenuItem value="0">Sunday</ModalContentMenuItem>
                                <ModalContentMenuItem value="1">Monday</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">Tuesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">Wednesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">Thursday</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">Friday</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">Saturday</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST summer: End week in month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstSummerShiftEndCountDayOfWeekInMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstSummerShiftEndCountDayOfWeekInMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstSummerShiftEndCountDayOfWeekInMonth && errors.nDstSummerShiftEndCountDayOfWeekInMonth)}
                              >
                                <ModalContentMenuItem value="1">1st</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">2nd</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">3rd</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">4th</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">5th</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST summer: End time"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="strDstSummerShiftEndTime"
                              touched={touched}
                              errors={errors}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                              <ModalTimePicker
                                value={dstSummerEndTime}
                                onChange={(value) => {
                                  const newTimeString = formatTimeString(value);
                                  setFieldValue("strDstSummerShiftEndTime", newTimeString);
                                }}
                                ampm={false}
                                views={["hours", "minutes", "seconds"]}
                                slotProps={ModalTimePickerSlotProps}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Box>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Divider */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nDstWinterShiftedMinutes */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <SmartParameter
                          value="DST winter: Shifted minutes"
                          helpReference="HandleTiming"
                          helpText=""
                          helpLink=""
                          fieldName="nDstWinterShiftedMinutes"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nDstWinterShiftedMinutes" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nDstWinterShiftedStart */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                          <Box>
                            <SmartParameter
                              value="DST winter: Start month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstWinterShiftStartNumberOfMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstWinterShiftStartNumberOfMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstWinterShiftStartNumberOfMonth && errors.nDstWinterShiftStartNumberOfMonth)}
                              >
                                <ModalContentMenuItem value="1">January</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">February</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">March</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">April</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">May</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">June</ModalContentMenuItem>
                                <ModalContentMenuItem value="7">July</ModalContentMenuItem>
                                <ModalContentMenuItem value="8">August</ModalContentMenuItem>
                                <ModalContentMenuItem value="9">September</ModalContentMenuItem>
                                <ModalContentMenuItem value="10">October</ModalContentMenuItem>
                                <ModalContentMenuItem value="11">November</ModalContentMenuItem>
                                <ModalContentMenuItem value="12">December</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST winter: Start day of week"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstWinterShiftStartDayOfWeek"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstWinterShiftStartDayOfWeek")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstWinterShiftStartDayOfWeek && errors.nDstWinterShiftStartDayOfWeek)}
                              >
                                <ModalContentMenuItem value="0">Sunday</ModalContentMenuItem>
                                <ModalContentMenuItem value="1">Monday</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">Tuesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">Wednesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">Thursday</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">Friday</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">Saturday</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST winter: Start week in month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstWinterShiftStartCountDayOfWeekInMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstWinterShiftStartCountDayOfWeekInMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstWinterShiftStartCountDayOfWeekInMonth && errors.nDstWinterShiftStartCountDayOfWeekInMonth)}
                              >
                                <ModalContentMenuItem value="1">1st</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">2nd</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">3rd</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">4th</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">5th</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST winter: Start time"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="strDstWinterShiftStartTime"
                              touched={touched}
                              errors={errors}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                              <ModalTimePicker
                                value={dstWinterStartTime}
                                onChange={(value) => {
                                  const newTimeString = formatTimeString(value);
                                  setFieldValue("strDstWinterShiftStartTime", newTimeString);
                                }}
                                ampm={false}
                                views={["hours", "minutes", "seconds"]}
                                slotProps={ModalTimePickerSlotProps}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Box>
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Parameter configuration */}
                      {/* nDstWinterShiftedEnd */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                          <Box>
                            <SmartParameter
                              value="DST winter: End month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstWinterShiftEndNumberOfMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstWinterShiftEndNumberOfMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstWinterShiftEndNumberOfMonth && errors.nDstWinterShiftEndNumberOfMonth)}
                              >
                                <ModalContentMenuItem value="1">January</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">February</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">March</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">April</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">May</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">June</ModalContentMenuItem>
                                <ModalContentMenuItem value="7">July</ModalContentMenuItem>
                                <ModalContentMenuItem value="8">August</ModalContentMenuItem>
                                <ModalContentMenuItem value="9">September</ModalContentMenuItem>
                                <ModalContentMenuItem value="10">October</ModalContentMenuItem>
                                <ModalContentMenuItem value="11">November</ModalContentMenuItem>
                                <ModalContentMenuItem value="12">December</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST winter: End day of week"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstWinterShiftEndDayOfWeek"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstWinterShiftEndDayOfWeek")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstWinterShiftEndDayOfWeek && errors.nDstWinterShiftEndDayOfWeek)}
                              >
                                <ModalContentMenuItem value="0">Sunday</ModalContentMenuItem>
                                <ModalContentMenuItem value="1">Monday</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">Tuesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">Wednesday</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">Thursday</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">Friday</ModalContentMenuItem>
                                <ModalContentMenuItem value="6">Saturday</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST winter: End week in month"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="nDstWinterShiftEndCountDayOfWeekInMonth"
                              touched={touched}
                              errors={errors}
                            />
                            <FormControl fullWidth>
                              <Select
                                {...getFieldProps("nDstWinterShiftEndCountDayOfWeekInMonth")}
                                input={<ModalContentOutlinedInput />}
                                error={Boolean(touched.nDstWinterShiftEndCountDayOfWeekInMonth && errors.nDstWinterShiftEndCountDayOfWeekInMonth)}
                              >
                                <ModalContentMenuItem value="1">1st</ModalContentMenuItem>
                                <ModalContentMenuItem value="2">2nd</ModalContentMenuItem>
                                <ModalContentMenuItem value="3">3rd</ModalContentMenuItem>
                                <ModalContentMenuItem value="4">4th</ModalContentMenuItem>
                                <ModalContentMenuItem value="5">5th</ModalContentMenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box>
                            <SmartParameter
                              value="DST winter: End time"
                              helpReference="HandleTiming"
                              helpText=""
                              helpLink=""
                              fieldName="strDstWinterShiftEndTime"
                              touched={touched}
                              errors={errors}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enCA}>
                              <ModalTimePicker
                                value={dstWinterEndTime}
                                onChange={(value) => {
                                  const newTimeString = formatTimeString(value);
                                  setFieldValue("strDstWinterShiftEndTime", newTimeString);
                                }}
                                ampm={false}
                                views={["hours", "minutes", "seconds"]}
                                slotProps={ModalTimePickerSlotProps}
                              />
                            </LocalizationProvider>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  ) : null}
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
